import React, { useEffect, useState } from "react";
import { 
  Card, Table, Button, Modal, Form, Input, DatePicker, 
  Select, Space, Tag, message, Typography, Popconfirm, Tooltip 
} from "antd";
import { 
  PlusOutlined, RocketOutlined, CalendarOutlined, 
  EditOutlined, DeleteOutlined 
} from "@ant-design/icons";
import axiosInstance from "../../services/axiosServicesVisit";
import { useDispatch, useSelector } from "react-redux";
import { getSamples } from "../../redux/slices/samples/samplesThunk";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const MarketingCampaignsPage = () => {
  const dispatch = useDispatch();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  const samplesList = useSelector((state) => state.samples.samples?.data || []);

  useEffect(() => {
    fetchCampaigns();
    dispatch(getSamples({ page: 1, limit: 1000 }));
  }, [dispatch]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/v1/marketing-campaigns");
      setCampaigns(res.data);
    } catch (e) {
      message.error("Erreur lors du chargement des campagnes");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (record = null) => {
    if (record) {
      setEditingId(record.id);
      form.setFieldsValue({
        name: record.name,
        description: record.description,
        period: [dayjs(record.startDate), dayjs(record.endDate)],
        sampleIds: record.samples?.map(s => s.id)
      });
    } else {
      setEditingId(null);
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        name: values.name,
        description: values.description,
        startDate: values.period[0].toISOString(),
        endDate: values.period[1].toISOString(),
        samples: values.sampleIds.map(id => ({ id }))
      };

      if (editingId) {
        await axiosInstance.patch(`/v1/marketing-campaigns/${editingId}`, payload);
        message.success("Action Marketing modifiée !");
      } else {
        await axiosInstance.post("/v1/marketing-campaigns", payload);
        message.success("Action Marketing créée !");
      }

      setIsModalVisible(false);
      fetchCampaigns();
    } catch (e) {
      message.error("Une erreur est survenue lors de l'enregistrement");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/v1/marketing-campaigns/${id}`);
      message.success("Action supprimée avec succès");
      fetchCampaigns();
    } catch (e) {
      message.error("Erreur lors de la suppression");
    }
  };

  const columns = [
    {
      title: "Nom de l'Action",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong><RocketOutlined /> {text}</Text>
    },
    {
      title: "Période",
      key: "period",
      render: (_, record) => (
        <span>
          <CalendarOutlined /> {dayjs(record.startDate).format("DD/MM/YYYY")} au {dayjs(record.endDate).format("DD/MM/YYYY")}
        </span>
      )
    },
    {
      title: "Échantillons",
      dataIndex: "samples",
      key: "samples",
      render: (samples) => (
        <Space wrap>
          {samples?.map(s => <Tag color="blue" key={s.id}>{s.name}</Tag>)}
        </Space>
      )
    },
    {
      title: "Statut",
      dataIndex: "isActive",
      render: (active) => <Tag color={active ? "green" : "red"}>{active ? "ACTIVE" : "TERMINÉE"}</Tag>
    },
    {
      title: "Actions",
      key: "actions",
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Modifier">
            <Button 
                icon={<EditOutlined />} 
                onClick={() => handleOpenModal(record)} 
                type="text" 
                style={{ color: '#1890ff' }}
            />
          </Tooltip>

          <Popconfirm
            title="Supprimer cette action ?"
            description="Cela ne supprimera pas les données des rapports passés."
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button 
                icon={<DeleteOutlined />} 
                danger 
                type="text" 
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="layout-content">
      <Card
        title={<Title level={4}>Gestion des Actions Spéciales Marketing</Title>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
            Créer une Action
          </Button>
        }
      >
        <Table 
          dataSource={campaigns} 
          columns={columns} 
          rowKey="id" 
          loading={loading} 
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingId ? "Modifier l'Action Marketing" : "Nouvelle Action Marketing"}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        okText={editingId ? "Sauvegarder" : "Créer"}
        cancelText="Annuler"
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item 
            name="name" 
            label="Nom de l'opération" 
            rules={[{ required: true, message: 'Nom requis' }]}
          >
            <Input placeholder="Ex: Lancement Vital Arteres Janvier" />
          </Form.Item>
          
          <Form.Item 
            name="period" 
            label="Dates de validité" 
            rules={[{ required: true, message: 'Période requise' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item 
            name="sampleIds" 
            label="Échantillons boostés" 
            rules={[{ required: true, message: 'Sélectionnez au moins un échantillon' }]}
          >
            <Select mode="multiple" placeholder="Sélectionner les produits...">
              {samplesList.map(s => (
                <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="description" label="Notes / Objectifs">
            <Input.TextArea rows={3} placeholder="Détails de l'opération..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MarketingCampaignsPage;